/**
 * Réception d'e-factures.
 *
 * La synchronisation elle-même — l'appel à SUPER PDP — ne vit pas ici :
 * c'est un appel HTTP externe, pas une opération sur la base, et elle suit le
 * même chemin que les autres Edge Functions du dépôt (`generate-pdf`,
 * `send-quote`…), invoquée directement depuis un hook. Ce fichier couvre ce
 * qui se passe une fois la facture rapatriée : la lister, la classer.
 */
import { z } from "zod";
import { ActionLevel } from "../../levels.js";
import { defineService } from "../../service.js";
import type { ExecutionContext } from "../../context.js";
import type { ReceivedInvoice, ReceivedInvoiceStatus, ReceptionStatus } from "./types.js";

export const listReceivedInvoicesInput = z.object({
  confirmationId: z.string().optional(),
  status: z.enum(["a_traiter", "classee"]).optional(),
});

export type ListReceivedInvoicesInput = z.infer<typeof listReceivedInvoicesInput>;

export const classifyReceivedInvoiceInput = z.object({
  confirmationId: z.string().optional(),
  receivedInvoiceId: z.string().uuid(),
  projectId: z.string().uuid().nullish(),
  categoryId: z.string().uuid().nullish(),
});

export type ClassifyReceivedInvoiceInput = z.infer<typeof classifyReceivedInvoiceInput>;

export const linkReceivedInvoiceSupplierInput = z.object({
  confirmationId: z.string().optional(),
  receivedInvoiceId: z.string().uuid(),
  supplierId: z.string().uuid().nullable(),
});

export type LinkReceivedInvoiceSupplierInput = z.infer<
  typeof linkReceivedInvoiceSupplierInput
>;

/**
 * Raccorder l'entreprise à SUPER PDP.
 *
 * `clientId`/`clientSecret` viennent de la fiche « Applications » que le
 * patron crée lui-même sur superpdp.tech — voir la documentation SUPER PDP,
 * grant Client Credentials. Le fournisseur n'est pas un champ : seul
 * SUPER PDP est câblé aujourd'hui, l'exposer laisserait croire à un choix
 * qui n'existe pas encore.
 */
export const configureReceptionInput = z.object({
  confirmationId: z.string().optional(),
  clientId: z.string().trim().min(1, "L'identifiant client SUPER PDP est requis"),
  clientSecret: z.string().trim().min(1, "Le secret client SUPER PDP est requis"),
});

export type ConfigureReceptionInput = z.infer<typeof configureReceptionInput>;

export interface PurchasesRepository {
  list(ctx: ExecutionContext, status?: ReceivedInvoiceStatus): Promise<ReceivedInvoice[]>;
  /** Retourne l'identifiant de la dépense créée. */
  classify(
    ctx: ExecutionContext,
    receivedInvoiceId: string,
    projectId: string | null,
    categoryId: string | null,
  ): Promise<string>;
  linkSupplier(
    ctx: ExecutionContext,
    receivedInvoiceId: string,
    supplierId: string | null,
  ): Promise<void>;
  getReceptionStatus(ctx: ExecutionContext): Promise<ReceptionStatus>;
  configureReception(
    ctx: ExecutionContext,
    clientId: string,
    clientSecret: string,
  ): Promise<void>;
}

export function createPurchasesServices(repo: PurchasesRepository) {
  const listerFacturesRecues = defineService<ListReceivedInvoicesInput, ReceivedInvoice[]>({
    name: "lister_factures_recues",
    level: ActionLevel.Read,
    handler: (ctx, input) => repo.list(ctx, input.status),
  });

  /**
   * Rattacher un fournisseur.
   *
   * Réversible, et sans confirmation : un rattachement se corrige d'un clic,
   * et n'a d'effet que sur la lecture — il ne crée ni n'envoie rien.
   */
  const rattacherFournisseur = defineService<
    LinkReceivedInvoiceSupplierInput,
    void
  >({
    name: "rattacher_fournisseur_facture_recue",
    level: ActionLevel.ReversibleWrite,
    allowedRoles: ["patron", "conducteur", "assistant"],
    handler: (ctx, input) => repo.linkSupplier(ctx, input.receivedInvoiceId, input.supplierId),
  });

  /**
   * Classer une facture reçue : elle devient une dépense.
   *
   * N2 et non N3 : contrairement à l'envoi d'un devis ou d'une facture, cette
   * action ne sort rien de l'entreprise et ne l'engage envers personne — elle
   * range une pièce déjà reçue dans la comptabilité. Le geste se corrige
   * comme n'importe quelle dépense mal saisie.
   */
  const classerEnDepense = defineService<ClassifyReceivedInvoiceInput, string>({
    name: "classer_facture_recue",
    level: ActionLevel.ReversibleWrite,
    allowedRoles: ["patron", "conducteur", "assistant"],
    handler: (ctx, input) =>
      repo.classify(
        ctx,
        input.receivedInvoiceId,
        input.projectId ?? null,
        input.categoryId ?? null,
      ),
  });

  const lireStatutReception = defineService<{ confirmationId?: string }, ReceptionStatus>({
    name: "lire_statut_reception_efactures",
    level: ActionLevel.Read,
    handler: (ctx) => repo.getReceptionStatus(ctx),
  });

  /**
   * N2, réservé au patron — même restriction que
   * `enregistrer_parametres_entreprise` : ces identifiants engagent
   * l'entreprise sur chaque facture qui entrera par ce canal.
   */
  const configurerReception = defineService<ConfigureReceptionInput, void>({
    name: "configurer_reception_efactures",
    level: ActionLevel.ReversibleWrite,
    allowedRoles: ["patron"],
    handler: (ctx, input) => repo.configureReception(ctx, input.clientId, input.clientSecret),
  });

  return {
    listerFacturesRecues,
    rattacherFournisseur,
    classerEnDepense,
    lireStatutReception,
    configurerReception,
  };
}

export type PurchasesServices = ReturnType<typeof createPurchasesServices>;
